import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CreditCard, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InsuranceForm({ token, insurance, onUpdate }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    carrier_name: '',
    policy_number: '',
    group_number: '',
    deductible: 0,
    deductible_met: 0,
    coinsurance_percentage: 20,
    copay: 0,
    out_of_pocket_max: 5000,
    out_of_pocket_met: 0
  });

  useEffect(() => {
    if (insurance) {
      setFormData({
        carrier_name: insurance.carrier_name || '',
        policy_number: insurance.policy_number || '',
        group_number: insurance.group_number || '',
        deductible: insurance.deductible || 0,
        deductible_met: insurance.deductible_met || 0,
        coinsurance_percentage: insurance.coinsurance_percentage || 20,
        copay: insurance.copay || 0,
        out_of_pocket_max: insurance.out_of_pocket_max || 5000,
        out_of_pocket_met: insurance.out_of_pocket_met || 0
      });
    }
  }, [insurance]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = insurance ? `${API}/insurance/${insurance.id}` : `${API}/insurance`;
      const method = insurance ? 'put' : 'post';
      
      const response = await axios[method](endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save insurance information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Card className="border-0 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <CreditCard className="w-6 h-6" />
          Insurance Information
        </CardTitle>
        <CardDescription>
          Provide your insurance details to get accurate cost estimates
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Carrier Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Carrier Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="carrier_name">Insurance Carrier *</Label>
                <Input
                  id="carrier_name"
                  data-testid="carrier-name-input"
                  placeholder="e.g., Blue Cross Blue Shield"
                  value={formData.carrier_name}
                  onChange={(e) => handleChange('carrier_name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="policy_number">Policy Number *</Label>
                <Input
                  id="policy_number"
                  data-testid="policy-number-input"
                  placeholder="e.g., ABC123456789"
                  value={formData.policy_number}
                  onChange={(e) => handleChange('policy_number', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group_number">Group Number</Label>
                <Input
                  id="group_number"
                  data-testid="group-number-input"
                  placeholder="Optional"
                  value={formData.group_number}
                  onChange={(e) => handleChange('group_number', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Coverage Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Coverage Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deductible">Annual Deductible ($)</Label>
                <Input
                  id="deductible"
                  data-testid="deductible-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.deductible}
                  onChange={(e) => handleChange('deductible', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deductible_met">Deductible Already Met ($)</Label>
                <Input
                  id="deductible_met"
                  data-testid="deductible-met-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.deductible_met}
                  onChange={(e) => handleChange('deductible_met', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="coinsurance_percentage">Coinsurance (% you pay)</Label>
                <Input
                  id="coinsurance_percentage"
                  data-testid="coinsurance-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.coinsurance_percentage}
                  onChange={(e) => handleChange('coinsurance_percentage', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="copay">Copay ($)</Label>
                <Input
                  id="copay"
                  data-testid="copay-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.copay}
                  onChange={(e) => handleChange('copay', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="out_of_pocket_max">Out-of-Pocket Maximum ($)</Label>
                <Input
                  id="out_of_pocket_max"
                  data-testid="oop-max-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.out_of_pocket_max}
                  onChange={(e) => handleChange('out_of_pocket_max', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="out_of_pocket_met">Out-of-Pocket Already Met ($)</Label>
                <Input
                  id="out_of_pocket_met"
                  data-testid="oop-met-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.out_of_pocket_met}
                  onChange={(e) => handleChange('out_of_pocket_met', parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="save-insurance-button"
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 gap-2"
            disabled={isLoading}
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Saving...' : 'Save Insurance Information'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}